import React, { useState } from 'react';
import { fileAPI } from '../../services/api';

const GalleryTab = ({ person, updateField }) => {
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideos, setUploadingVideos] = useState(false);
  const [activeGallery, setActiveGallery] = useState('images');
  const [preview, setPreview] = useState({ type: null, src: '' });

  const galleryImages = person.galleryImages || [];
  
  const isTikTokPath = (val) => {
    const s = typeof val === 'string' ? val : (val && (val.filePath || val.path || val.filename || ''));
    if (!s) return false;
    const base = s.split(/[\\/]/).pop() || '';
    return /tiktok|TikTokVid|\/TikTok\//i.test(s) || /^tiktok_/i.test(base);
  };
  
  const isVideoFile = (val) => {
    const s = (typeof val === 'string' ? val : (val && (val.filePath || val.path || val.filename || ''))) || '';
    const base = s.toString().split(/[\\/]/).pop() || '';
    return /\.(mp4|mov|mkv|webm|avi)$/i.test(base);
  };
  
  const tiktokBasenames = new Set(
    (person.tiktokVideos || [])
      .map(v => (typeof v === 'string' ? v : (v.url || v.filePath || v.path || '')))
      .filter(Boolean)
      .map(s => s.toString().split(/[\\/]/).filter(Boolean).pop())
  );

  const uniqueNonTikTok = [];
  const seen = new Set();
  (person.galleryVideos || []).forEach((v) => {
    if (!v || isTikTokPath(v)) return;
    if (!isVideoFile(v)) return;
    const key = (typeof v === 'string' ? v : (v.filePath || v.path || v.filename || ''))
      .toString()
      .split(/[\\/]/)
      .filter(Boolean)
      .pop() || '';
    if (tiktokBasenames.has(key)) return;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueNonTikTok.push(v);
    }
  });
  const galleryVideos = uniqueNonTikTok;

  const toUrl = (value) => {
    if (!value) return '';
    if (value.startsWith('/uploads/')) return value;
    const basename = value.split('/').pop();
    return `/uploads/${basename}`;
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadingImages(true);
    try {
      const uploadPromises = files.map(file => fileAPI.upload(file));
      const responses = await Promise.all(uploadPromises);
      const newImages = responses.map(response => response.data.stored_filename);
      updateField('galleryImages', [...galleryImages, ...newImages]);
    } catch (error) {
      console.error('Image upload error:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadingVideos(true);
    try {
      const uploadPromises = files.map(file => fileAPI.upload(file));
      const responses = await Promise.all(uploadPromises);
      const newVideos = responses.map(response => response.data.stored_filename);
      updateField('galleryVideos', [...galleryVideos, ...newVideos]);
    } catch (error) {
      console.error('Video upload error:', error);
    } finally {
      setUploadingVideos(false);
    }
  };

  const handleRemoveImage = (index) => {
    updateField('galleryImages', galleryImages.filter((_, i) => i !== index));
  };

  const handleRemoveVideo = (index) => {
    updateField('galleryVideos', galleryVideos.filter((_, i) => i !== index));
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4 p-4">
      {/* Gallery Type Selector */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveGallery('images')}
          className={`tech-btn ${activeGallery === 'images' ? 'tech-btn-primary' : 'tech-btn-secondary'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          IMAGES ({galleryImages.length})
        </button>
        <button
          onClick={() => setActiveGallery('videos')}
          className={`tech-btn ${activeGallery === 'videos' ? 'tech-btn-primary' : 'tech-btn-secondary'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          VIDEOS ({galleryVideos.length})
        </button>
      </div>

      {activeGallery === 'images' && (
        <>
          <div className="tech-card p-4">
            <div className="border-2 border-dashed border-tech-border rounded-lg p-4 text-center hover:border-tech-accent/50 transition">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={uploadingImages}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <svg className="w-8 h-8 text-tech-textMuted mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-tech-textMuted">
                  {uploadingImages ? 'UPLOADING...' : 'CLICK TO UPLOAD IMAGES'}
                </p>
              </label>
            </div>
          </div>

          <div className="tech-card p-4">
            {galleryImages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-tech-textDim text-sm">NO IMAGES UPLOADED</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {galleryImages.map((image, index) => (
                  <div key={index} className="relative group">
                    <button onClick={() => setPreview({ type: 'image', src: toUrl(image) })} className="block w-full">
                      <div className="w-full aspect-square bg-tech-surface rounded overflow-hidden">
                        <img src={toUrl(image)} alt="" className="w-full h-full object-cover" />
                      </div>
                    </button>
                    <button
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-2 right-2 p-2 bg-tech-danger text-white rounded opacity-0 group-hover:opacity-100 transition"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {activeGallery === 'videos' && (
        <>
          <div className="tech-card p-4">
            <div className="border-2 border-dashed border-tech-border rounded-lg p-4 text-center hover:border-tech-accent/50 transition">
              <input
                type="file"
                accept="video/*"
                multiple
                onChange={handleVideoUpload}
                disabled={uploadingVideos}
                className="hidden"
                id="video-upload"
              />
              <label htmlFor="video-upload" className="cursor-pointer">
                <svg className="w-8 h-8 text-tech-textMuted mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-tech-textMuted">
                  {uploadingVideos ? 'UPLOADING...' : 'CLICK TO UPLOAD VIDEOS'}
                </p>
              </label>
            </div>
          </div>

          <div className="tech-card p-4">
            {galleryVideos.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-tech-textDim text-sm">NO VIDEOS UPLOADED</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {galleryVideos.map((video, index) => (
                  <div key={index} className="relative group">
                    <button onClick={() => setPreview({ type: 'video', src: toUrl(video) })} className="block w-full">
                      <div className="aspect-video bg-tech-surface rounded overflow-hidden">
                        <video src={toUrl(video)} className="w-full h-full object-cover" />
                      </div>
                    </button>
                    <button
                      onClick={() => handleRemoveVideo(index)}
                      className="absolute top-2 right-2 p-2 bg-tech-danger text-white rounded opacity-0 group-hover:opacity-100 transition"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Preview Modal */}
      {preview.type && (
        <div className="fixed inset-0 bg-tech-bg/90 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setPreview({ type: null, src: '' })}>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreview({ type: null, src: '' })}
              className="absolute top-4 right-4 p-2 text-tech-textMuted hover:text-tech-text"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="bg-tech-surface rounded overflow-hidden">
              {preview.type === 'image' ? (
                <img src={preview.src} alt="" className="w-full h-auto" />
              ) : (
                <video src={preview.src} controls autoPlay className="w-full h-auto" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryTab;
